import React from 'react';
import classNames from 'classnames';
import { compact } from '@vds/core';
import { Text, TextSize, TextColor, TextWeight, TextProps } from '@components/text/Text';
import './Link.scss';

export type LinkTextSize = Extract<TextSize, 'small' | 'medium' | 'large'>;
export type LinkTextWeight = Exclude<TextWeight, 'black' | 'light'>;
export type LinkTextColor = Extract<TextColor, 'primary' | 'secondary' | 'tertiary' | 'links'>;

export interface LinkProps extends Omit<TextProps, 'component' | 'size' | 'weight' | 'color'> {
  href: React.AnchorHTMLAttributes<HTMLAnchorElement>['href'];
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  onBlur?: (event: React.FocusEvent) => void;
  onClick?: (event: React.MouseEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
  size?: LinkTextSize;
  weight?: LinkTextWeight;
  color?: LinkTextColor;
  underline?: boolean;
  disabled?: boolean;
  tabIndex?: number;
  isFooter?: boolean;
  linkComponent?: React.ElementType;
}

/**
 * Link Component
 * @param id - A unique identifier for the element.
 * @param href - The URL that the hyperlink points to.
 * @param target - Defines the frame or window to open the anchor defined on href.
 * @param onBlur - Callback triggered when when the element loses focus.
 * @param onClick - Callback fired when Link is clicked.
 * @param onFocus - Callback triggered when the element gains focus.
 * @param size - Defines the size of the text.
 * @param weight - Defines the weight of the text.
 * @param color - Defines the color of the text.
 * @param underline - If true, it will show an underline by default and hover will hide the underline.
 * @param children - Any React.ReactNode component.
 * @param align - Defines the alignment of the text.
 * @param lineClamp - Visually truncate the text to the specified number of lines.
 * @param overflowWrap - How truncation is handled when text overflows the line.
 * @param className - Any additional classnames.
 * @param disabled - If true, the component is disabled.
 * @param tabIndex - Defines the index number of key navigation.
 * @param linkComponent - Custom component to render instead of a native `<a>`. Must accept anchor-like props (`href`, `target`, etc.) and forward a ref (e.g. a Next.js `Link` or a React Router wrapper that maps `href` to `to`).
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      id,
      href,
      target,
      underline = false,
      onBlur,
      onClick,
      onFocus,
      size = 'large',
      weight = 'semibold',
      color = 'links',
      isFooter,
      children,
      className,
      disabled,
      tabIndex = disabled ? -1 : 0,
      linkComponent,
      ...rest
    },
    ref,
  ) => {
    const validProps = compact({
      ...rest,
      href,
      target,
      ref,
      onBlur,
      onClick,
      onFocus,
      tabIndex,
      ['aria-disabled']: disabled,
    });

    return (
      <Text
        id={id}
        component={disabled ? 'span' : linkComponent || 'a'}
        variant="link"
        size={size}
        weight={weight}
        color={color}
        className={classNames(
          'vds-link',
          {
            'vds-link--underline': underline,
            'vds-link--disabled': disabled,
            'vds-link--footer': isFooter,
          },
          className,
        )}
        {...validProps}>
        {children}
      </Text>
    );
  },
);
